import React, { ForwardedRef } from 'react'
import { Controller } from 'react-hook-form'
import Input from '@atoms/Input/Input'
import SelectBox from '@atoms/SelectBox/SelectBox'
import RadioButton from '@atoms/RadioButton/RadioButton'
import Checkbox from '@atoms/Checkbox/Checkbox'
import TextArea from '@atoms/TextArea/TextArea'
import Captcha from '@molecules/Captcha/Captcha'
import { FieldProps } from './Field.types'

const Field = React.forwardRef(
  (
    {
      className,
      config,
      onInit,
      control,
      error,
      isDisabled,
      readOnly,
      prefix = '',
      additionalProps,
      inputClassName
    }: FieldProps,
    ref: ForwardedRef<any>
  ) => {
    return (
      <Controller
        name={`${prefix}${config.name}`}
        control={control}
        rules={config.rules}
        defaultValue={config.defaultValues || config.defaultValue || ''}
        render={(field) => {
          const fieldWithRef = { ...field, ref }
          switch (config.type) {
            case 'text':
            case 'password':
            case 'tel':
            case 'email':
            case 'hidden':
            case 'date':
              return (
                <Input
                  {...fieldWithRef}
                  {...additionalProps}
                  id={`${prefix}${config.id}`}
                  className={className}
                  labelText={config.label}
                  placeholder={config.placeholder}
                  type={config.type}
                  hasError={!!error?.message}
                  errorMessage={error?.message}
                  isDisabled={isDisabled}
                  readOnly={readOnly}
                  onUpdatePrefix={config.onUpdatePrefix}
                  initialPrefix={config.initialPrefix}
                  isDarkMode={additionalProps?.isDarkMode}
                  inputClassName={inputClassName}
                  onChange={(value) => {
                    field.onChange(value)
                    config.onChange && config?.onChange(value)
                  }}
                  onBlur={() => {
                    field.onBlur()
                    config.onBlur && config?.onBlur()
                  }}
                />
              )
            case 'select':
              return (
                <SelectBox
                  {...fieldWithRef}
                  {...config}
                  className={className}
                  id={
                    prefix && config.id ? `${prefix}${config.id}` : config.name
                  }
                  hasError={!!error?.message}
                  disabled={isDisabled}
                  onInit={onInit}
                  errorMessage={error?.message}
                  onChange={field.onChange}
                />
              )
            case 'radio':
              return (
                <>
                  {config.options?.map((opt) => {
                    const { extraClassName, ...rest } = opt

                    return (
                      <RadioButton
                        {...fieldWithRef}
                        {...additionalProps}
                        {...rest}
                        key={opt.id}
                        type={config.type}
                        className={`${opt.className} ${extraClassName}`}
                        onChange={field.onChange}
                        checkedValue={
                          (config.name && control?.getValues(config.name)) ||
                          config.defaultValues
                        }
                      />
                    )
                  })}
                </>
              )
            case 'checkbox':
              return (
                <Checkbox
                  {...fieldWithRef}
                  {...additionalProps}
                  {...config}
                  checkboxClassName={inputClassName}
                  className={className}
                  hasError={!!error?.message}
                  onChange={field.onChange}
                  disabled={isDisabled}
                />
              )
            case 'textarea':
              return (
                <TextArea
                  {...fieldWithRef}
                  id={`${prefix}${config.id}`}
                  rows={config.rows}
                  cols={config.cols}
                  label={config.label}
                  readOnly={readOnly}
                  placeholder={config.placeholder}
                  error={!!error?.message}
                  errorMessage={error?.message}
                  resizeable={config?.resizeable}
                />
              )
            case 'captcha':
              return (
                <>
                  <Captcha />
                  <Input
                    {...fieldWithRef}
                    {...additionalProps}
                    id={`${prefix}${config.id}`}
                    className={`${className} w-full md:w-52`}
                    labelText={config.label}
                    hasError={!!error?.message}
                    errorMessage={error?.message}
                    isDisabled={isDisabled}
                    readOnly={readOnly}
                    onChange={(value) => {
                      field.onChange(value)
                      config.onChange && config?.onChange(value)
                    }}
                    onBlur={() => {
                      field.onBlur()
                      config.onBlur && config?.onBlur()
                    }}
                  />
                </>
              )

            default:
              return <input type={config.type} {...field} ref={ref} />
          }
        }}
      />
    )
  }
)

Field.displayName = 'Field'

export default Field
